// Reusable info tooltip: a small circular "i" icon that reveals a dark popover card
// with a title and rich body. Shows on hover (desktop) and tap (mobile); focusable
// and Esc-dismissable for keyboard users; closes on outside click or mouse leave.
//
// Built on MUI (already a dependency, same as the inline InfoTip used across the GTM
// surfaces) and the app's THEME tokens, so it matches the existing dark-tooltip look
// rather than introducing a new design system. The MUI hover/focus/touch listeners
// are disabled and the open state is driven manually, so a single tap opens it on
// mobile and the popover is fully keyboard controllable.
import React, { useState } from 'react';
import { Box, Typography, Tooltip, ClickAwayListener } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { THEME } from '../../pages/enterprise-ai/genesisData';

const InfoTooltip = ({ title, children, content, size = 14, sx = {} }) => {
  const [open, setOpen] = useState(false);
  const body = content != null ? content : children;

  const show = () => setOpen(true);
  const hide = () => setOpen(false);
  const toggle = () => setOpen((o) => !o);

  const popoverContent = (
    <Box sx={{ maxWidth: 320 }}>
      {title && (
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: THEME.textPrimary, mb: 0.5 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ fontSize: '12px', fontWeight: 400, lineHeight: 1.6, color: THEME.textSecondary }}>
        {body}
      </Box>
    </Box>
  );

  return (
    <ClickAwayListener onClickAway={hide}>
      <Tooltip
        open={open}
        onClose={hide}
        // We drive open/close ourselves so a mobile tap opens it and Esc/blur close it.
        disableHoverListener
        disableFocusListener
        disableTouchListener
        title={popoverContent}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              background: THEME.cardRaised,
              color: THEME.textSecondary,
              border: `1px solid ${THEME.borderStrong}`,
              borderRadius: '8px',
              maxWidth: 320,
              px: 1.5,
              py: 1.25,
              boxShadow: '0 6px 20px rgba(0,0,0,0.45)',
            },
          },
          arrow: { sx: { color: THEME.cardRaised, '&::before': { border: `1px solid ${THEME.borderStrong}` } } },
        }}
      >
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-label={title ? `More information: ${title}` : 'More information'}
          aria-expanded={open}
          onMouseEnter={show}
          onMouseLeave={hide}
          onClick={(e) => { e.stopPropagation(); toggle(); }}
          onFocus={show}
          onBlur={hide}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { hide(); }
            else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'help',
            borderRadius: '50%',
            outline: 'none',
            color: open ? THEME.mint : THEME.textMuted,
            '&:hover': { color: THEME.mint },
            '&:focus-visible': { boxShadow: `0 0 0 2px ${THEME.mintDim}` },
            ...sx,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: size, verticalAlign: 'middle', flexShrink: 0 }} />
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};

export default InfoTooltip;
